import type { NormalizedCacheObject } from '@apollo/client'
import { ApolloClient, ApolloLink, from, InMemoryCache } from '@apollo/client'
import { isPresent, VERSION } from '@mbfab/shared'
import { useEffect, useState } from 'react'

import { cleanTypeNameLink } from './apollo/clearTypeNameLink'
import { errorLink } from './apollo/errorLink'
import { httpLink } from './apollo/httpLink'
import { retryLink } from './apollo/retryLink'

const createAuthLink = (token: string | null, tenantId: string | null) => {
  return new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: token !== null ? `Bearer ${token}` : '',
        'x-tenant-id': isPresent(tenantId) ? tenantId : '',
      },
    }))

    return forward(operation)
  })
}

export const createApolloClient = async (token: string | null, tenantId: string | null) => {
  const cache = new InMemoryCache()

  const client = new ApolloClient({
    cache,
    link: from([cleanTypeNameLink, errorLink, createAuthLink(token, tenantId), retryLink, httpLink]),
    defaultOptions: {
      query: {
        fetchPolicy: 'network-only', // cache-and-networkが選べなかった
      },
    },
    name: 'mbfab-client',
    version: VERSION,
  })
  return client
}

export const useApolloClient = ({ tenantId, token }: { tenantId: string | null; token: string | null }) => {
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject> | undefined>(undefined)

  const awaitSetClient = async () => {
    const client = await createApolloClient(token, tenantId)
    setClient(client)
  }

  useEffect(() => {
    void awaitSetClient()
  }, [token, tenantId])

  return client
}
